/**
 * Аккаунты — все люди платформы одним списком: команда Skrezo, владельцы и
 * врачи клиник. Поиск по ФИО и телефону, фильтры «кто» и «работает /
 * исключён», блокировка аккаунта целиком (единственный способ отобрать
 * доступ ко всем клиникам сразу). Данные — GET admin/accounts.
 */

import {useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {type Account, accountsQueryOptions, unblockAccount} from '@services/accounts';
import {keys} from '@queries/keys';
import {useAuth} from '@hooks/useAuth';
import {useToast} from '@hooks/useToast';
import {SearchField} from '@components/SearchField';
import {Toast} from '@components/Toast';
import {Button} from '@components/ui/Button';
import {Loader} from '@components/ui/Loader';
import {cn} from '@lib/utils';

import {AccountRow} from './AccountRow';
import {BlockAccountModal} from './BlockAccountModal';

type Who = 'all' | 'clients' | 'team';
type Works = 'all' | 'active' | 'excluded';

const WHO: {key: Who; label: string}[] = [
  {key: 'all', label: 'Все'},
  {key: 'clients', label: 'Клиники'},
  {key: 'team', label: 'Команда'},
];
const WORKS: {key: Works; label: string}[] = [
  {key: 'all', label: 'Все'},
  {key: 'active', label: 'Работают'},
  {key: 'excluded', label: 'Исключены'},
];

export const Accounts = () => {
  const {team} = useAuth();
  const qc = useQueryClient();
  const {toast, notify} = useToast();
  const [query, setQuery] = useState('');
  const [who, setWho] = useState<Who>('all');
  const [works, setWorks] = useState<Works>('all');
  const [page, setPage] = useState(1);
  const [blocking, setBlocking] = useState<Account | null>(null);

  const req = {
    query,
    page,
    ...(who !== 'all' && {isPlatformAdmin: who === 'team'}),
    ...(works !== 'all' && {active: works === 'active'}),
  };
  const {data, isLoading} = useQuery(accountsQueryOptions(req));
  const unblock = useMutation({
    mutationFn: unblockAccount,
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: keys.accounts.all});
      void qc.invalidateQueries({queryKey: keys.clients.all});
      notify('Доступ возвращён — войти нужно заново');
    },
  });

  const list = data?.list ?? [];
  const pages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;

  const handleQuery = (v: string) => {
    setQuery(v);
    setPage(1);
  };
  const handleWho = (v: Who) => {
    setWho(v);
    setPage(1);
  };
  const handleWorks = (v: Works) => {
    setWorks(v);
    setPage(1);
  };
  const handleBlocked = () => {
    setBlocking(null);
    notify('Аккаунт заблокирован, сессии погашены', 2200);
  };

  return (
    <>
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur max-md:px-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-bold -tracking-[.02em]">Аккаунты</h1>
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            Все люди платформы{data ? ` · ${data.totalCount}` : ''}
          </div>
        </div>
        <Segment items={WHO} value={who} onChange={handleWho} />
      </header>

      <div className="max-w-[1080px] px-[30px] pb-[60px] pt-[22px] max-md:px-4">
        <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
          <SearchField
            value={query}
            onChange={handleQuery}
            placeholder="ФИО или телефон"
            className="h-10 min-w-[220px] flex-1 rounded-[10px] border border-skr-line bg-card px-3"
          />
          <Segment items={WORKS} value={works} onChange={handleWorks} />
        </div>

        {isLoading ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Loader label="Загружаем аккаунты…" />
          </div>
        ) : list.length === 0 ? (
          <div className="flex min-h-[240px] items-center justify-center text-[13.5px] text-muted-foreground">
            Никого не нашли
          </div>
        ) : (
          <div className="overflow-hidden rounded-[13px] border border-skr-line bg-card">
            {list.map((a, i) => (
              <AccountRow
                key={a.id}
                account={a}
                self={a.id === team.user?.id}
                pending={unblock.isPending}
                onBlock={setBlocking}
                onUnblock={(x) => unblock.mutate(x.id)}
                last={i === list.length - 1}
              />
            ))}
          </div>
        )}

        {pages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <Button
              variant="outline"
              className="h-9 rounded-[9px]"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Назад
            </Button>
            <span className="font-mono text-[13px] text-muted-foreground">
              {page} / {pages}
            </span>
            <Button
              variant="outline"
              className="h-9 rounded-[9px]"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Дальше
            </Button>
          </div>
        )}
      </div>

      <BlockAccountModal
        account={blocking}
        onClose={() => setBlocking(null)}
        onSaved={handleBlocked}
      />
      <Toast message={toast} />
    </>
  );
};

const Segment = <T extends string>({
  items,
  value,
  onChange,
}: {
  items: {key: T; label: string}[];
  value: T;
  onChange: (v: T) => void;
}) => (
  <div className="flex rounded-[10px] border border-skr-line bg-card p-[3px]">
    {items.map((it) => (
      <button
        key={it.key}
        onClick={() => onChange(it.key)}
        className={cn(
          'rounded-[8px] px-3 py-1.5 text-[12.5px] font-medium text-muted-foreground transition',
          value === it.key && 'bg-skr-head font-semibold text-foreground',
        )}
      >
        {it.label}
      </button>
    ))}
  </div>
);
